/**
 * Deep links for a case and one task on it (issue #274): `GET /api/v1/cases/{id}/links` and
 * `GET /api/v1/cases/{id}/links/cards/{card_id}`. The server builds every link from records it holds and drops the
 * ones the viewer's role may not see, so this module only fetches and groups. Owner: feat/act-or-link.
 */
import { queryOptions } from '@tanstack/react-query'
import { unwrap } from '../../lib/api/client'
import {
  type CaseLink,
  type CaseLinksOut,
  cardLinksApiV1CasesCaseIdLinksCardsCardIdGet,
  caseLinksApiV1CasesCaseIdLinksGet,
  type LinkGroup,
  type LinkKind,
  type MissingLink,
} from '../../lib/api/generated'

export type { CaseLink, CaseLinksOut, LinkGroup, LinkKind, MissingLink }

export const linkKeys = {
  all: ['case-links'] as const,
  caseLinks: (caseId: string, kinds?: readonly LinkKind[]): readonly string[] => ['case-links', caseId, 'case', ...(kinds ?? [])],
  card: (caseId: string, cardId: string): readonly string[] => ['case-links', caseId, 'card', cardId],
}

export const caseLinksQuery = (caseId: string, kinds?: readonly LinkKind[]) =>
  queryOptions({
    queryKey: linkKeys.caseLinks(caseId, kinds),
    queryFn: ({ signal }): Promise<CaseLinksOut> =>
      unwrap(
        caseLinksApiV1CasesCaseIdLinksGet({
          path: { case_id: caseId },
          query: kinds?.length ? { kind: [...kinds] } : undefined,
          signal,
        }),
      ),
    staleTime: 60_000,
    retry: 1,
  })

export const cardLinksQuery = (caseId: string, cardId: string) =>
  queryOptions({
    queryKey: linkKeys.card(caseId, cardId),
    queryFn: ({ signal }): Promise<CaseLinksOut> =>
      unwrap(cardLinksApiV1CasesCaseIdLinksCardsCardIdGet({ path: { case_id: caseId, card_id: cardId }, signal })),
    staleTime: 60_000,
    retry: 1,
  })

export const GROUP_LABEL: Record<LinkGroup, string> = {
  conduit: 'Conduit',
  remote: 'Remote access',
  network: 'Network',
  pbx: 'Phone system',
  zoho: 'Desk and CRM',
  money: 'Books and deals',
  carrier: 'Carrier status',
  phone: 'Call',
}

const GROUP_ORDER: LinkGroup[] = ['remote', 'conduit', 'network', 'pbx', 'phone', 'zoho', 'money', 'carrier']

/** Links grouped in a fixed order (remote access first: it is what a tech reaches for). */
export function groupLinks(links: CaseLink[]): { group: LinkGroup; links: CaseLink[] }[] {
  const by = new Map<LinkGroup, CaseLink[]>()
  for (const link of links) by.set(link.group, [...(by.get(link.group) ?? []), link])
  return GROUP_ORDER.filter((g) => by.has(g)).map((group) => ({ group, links: by.get(group) ?? [] }))
}

export const REASON_LABEL: Record<MissingLink['reason'], string> = {
  no_record: 'No record',
  unreachable: 'Unreachable',
  not_configured: 'Not set up',
  not_linkable: 'No link form',
}
